//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Chaos Mode Testing Composable
 *
 * Simulates random API failures and delays for robustness testing.
 * Enables chaos engineering practices in development/staging environments.
 *
 * Responsibilities:
 * - Simulate random HTTP errors (400, 401, 403, 404, 500, 502, 503, 504)
 * - Simulate timeout errors
 * - Add random latency to API requests (100ms-3000ms)
 * - Controlled via CHAOS environment variable
 * - Log all simulated failures for debugging
 */
export const useChaos = () => {
  const config = useRuntimeConfig();
  const isChaosEnabled = config.public.features.chaos;

  // Log chaos mode status on first use
  if (import.meta.client && isChaosEnabled) {
    console.log("Chaos enabled.");
  }

  //
  // ============================================================================
  // METHODS
  // ============================================================================
  //

  /**
   * Apply chaos effects to API request
   *
   * Purpose:
   * Randomly inject failures or latency into API calls for testing.
   * Only active when CHAOS=true and in client-side context.
   *
   * Responsibilities:
   * - 20% chance: Throw simulated HTTP error or timeout
   * - 60% chance: Add random delay (100-3000ms)
   * - Log all simulated effects for debugging
   * - Skip if chaos disabled or SSR context
   *
   * @param endpoint - API endpoint being called (for logging)
   * @param componentName - Optional component name (for logging)
   * @throws Error - Simulated HTTP or timeout error
   */
  const applyChaos = async (endpoint: string, componentName?: string): Promise<void> => {
    if (!isChaosEnabled || !import.meta.client) {
      return;
    }

    // Configurable chance of error simulation
    if (Math.random() < config.public.chaos.failureRate) {
      const errorTypes = [400, 401, 403, 404, 500, 502, 503, 504, "timeout"];
      const errorType = errorTypes[Math.floor(Math.random() * errorTypes.length)];

      const componentInfo = componentName ? ` on ${componentName} component` : "";
      console.log(`Chaos simulated error ${errorType}${componentInfo} (${endpoint})`);

      if (errorType === "timeout") {
        throw new Error("Request timeout - simulated by chaos mode");
      } else {
        const error = new Error(`HTTP ${errorType} error - simulated by chaos mode`) as Error & { statusCode?: number };
        error.statusCode = errorType as number;
        throw error;
      }
    }

    // 60% chance of delay simulation
    if (Math.random() < 0.6) {
      const delayRange = config.public.chaos.delayMax - config.public.chaos.delayMin;
      const delay = Math.floor(Math.random() * delayRange) + config.public.chaos.delayMin;
      const componentInfo = componentName ? ` for ${componentName}` : "";
      console.log(
        `Chaos simulated longer loading time${componentInfo} (${endpoint}) as ${delay} ms are added as delay.`,
      );

      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  };

  return {
    isChaosEnabled: readonly(ref(isChaosEnabled)),
    applyChaos,
  };
};
